import React from "react";
import PropTypes from "prop-types";
import { CircleCheck, TriangleAlert } from "lucide-react";
import Status from "components/common_components/Status";

/**
 * Last step: what is about to be created. When some values are not copied as they are, the user has to
 * agree before saving.
 */
const CopyConfirmStep = ({
    sourceFormClient,
    targetForm,
    attorneyName,
    fileNo,
    copiedCount,
    notCopiedCount,
    tooLongCount,
    addendumsCount,
    agreed,
    onAgreedChange,
}) => {
    const needsAgreement = notCopiedCount + tooLongCount > 0;

    return (
        <div className="cw-confirm">
            <h3>A new form will be created</h3>
            <dl className="cw-confirm__summary">
                <dt>Client</dt>
                <dd>{sourceFormClient.client?.fullName}</dd>
                <dt>Form</dt>
                <dd>
                    {targetForm.code} · Version {targetForm.version} <Status id="active" />
                </dd>
                <dt>Copied from</dt>
                <dd>Version {sourceFormClient.form?.version}, which stays as it is</dd>
                <dt>Status</dt>
                <dd>Open</dd>
                <dt>Attorney</dt>
                <dd>{attorneyName || "--"}</dd>
                <dt>Case</dt>
                <dd>{fileNo || "--"}</dd>
                <dt>Values copied</dt>
                <dd>{copiedCount}</dd>
                <dt>Addendums</dt>
                <dd>{addendumsCount}</dd>
            </dl>

            {needsAgreement ? (
                <div className="cw-callout cw-callout--warning" role="status">
                    <TriangleAlert size={20} />
                    <div>
                        <b>
                            {notCopiedCount > 0 && `${notCopiedCount} values will not be copied. `}
                            {tooLongCount > 0 && `${tooLongCount} values are copied but are too long for their field.`}
                        </b>
                        <label className="cw-agree">
                            <input type="checkbox" checked={agreed} onChange={(e) => onAgreedChange(e.target.checked)} />
                            <span>I reviewed the values that are not copied as they are, and I agree.</span>
                        </label>
                    </div>
                </div>
            ) : (
                <div className="cw-callout cw-callout--success" role="status">
                    <CircleCheck size={20} />
                    <div>
                        <b>Every value is copied as it is.</b>
                    </div>
                </div>
            )}
        </div>
    );
};

CopyConfirmStep.propTypes = {
    sourceFormClient: PropTypes.shape({
        client: PropTypes.shape({ fullName: PropTypes.string }),
        form: PropTypes.shape({ version: PropTypes.string }),
    }).isRequired,
    targetForm: PropTypes.shape({ code: PropTypes.string, version: PropTypes.string }).isRequired,
    attorneyName: PropTypes.string,
    fileNo: PropTypes.string,
    copiedCount: PropTypes.number.isRequired,
    notCopiedCount: PropTypes.number.isRequired,
    tooLongCount: PropTypes.number.isRequired,
    addendumsCount: PropTypes.number.isRequired,
    agreed: PropTypes.bool.isRequired,
    onAgreedChange: PropTypes.func.isRequired,
};

CopyConfirmStep.defaultProps = {
    attorneyName: null,
    fileNo: null,
};

export default CopyConfirmStep;
